<template>
	<v-form ref="form" @submit.prevent="submit" class="px-2">
		<!-- benefits  -->
		<v-dialog
			v-model="benefitsDialog"
			max-width="450"
			content-class="rounded-medium"
		>
			<v-form ref="benefitsForm" @submit.prevent="saveBenefitEntry">
				<v-card>
					<v-card-title class="justify-center">
						{{benefitsEntryId ? $t('edit') : $t('add')}} {{$t('Products.entry')}}
					</v-card-title>
					<v-card-text class="pb-2">
						<!-- lang tabs -->
						<v-lang-tabs
							v-if="multiLangInputs"
							v-model="benefitsFormLang"
							background-color="bgColor"
							class="mb-2"
						/>

						<v-row dense align="center">
							<v-col cols="12" class="d-flex align-center">
								<!-- image -->
								<bee-drop-img
									v-if="useBenefitImage"
									v-model="benefitsData.image"
									dashed
									border-width="2"
									:border-color="isSubmitted & !benefitsData.image ? '#f00' : '#ccc'"
									radius="11"
									:height="60"
									:width="60"
									contain
									blurable
									clearable
									hide-details
									hide-image-name
									hide-drag-drop-text
									disable-file-picker
									class="me-2"
									@remove-image="benefitsData.fileManagerImage = {}"
									@click="$eventBus.$emit('file-manager', { images: true }, (url, file) => {
										benefitsData.image = url;
										benefitsData.fileManagerImage = file;
									})"
								/>
								
								<!-- title -->
								<v-textarea
									v-model="benefitsData.title[benefitsFormLang]"
									:label="$t('inputs.title')"
									rows="1"
									no-resize
									auto-grow
									dense
									outlined
									hide-details
									:rules="rules.required"
									class="rounded-medium"
								></v-textarea>
							</v-col>

							<!-- description -->
							<v-col cols="12">
								<p class="text-body-1 font-wight-medium text-capitalize mb-1">
									{{$t('inputs.description')}}
								</p>
								<text-editor
									v-model="benefitsData.description[benefitsFormLang]"
									mini-tools
									hide-menubar
									:height="200"
									class="rounded-small mx-n1"
									:error="isSubmitted && !benefitsData.description[benefitsFormLang]"
								></text-editor>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions class="justify-end">
						<v-btn
							type="submit"
							color="primary"
							class="rounded-medium"
						>{{$t('save')}}</v-btn>
						<v-btn
							class="rounded-medium"
							@click="benefitsDialog = false;"
						>{{$t('cancel')}}</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- lang tabs -->
		<!-- :disable-non-selected="(isInputsChanged && Boolean($route.params.id)) || submitLoading" -->
		<v-lang-tabs
			v-if="multiLangInputs"
			v-model="lang"
			:checked-langs="acceptedToSaveLangs"
			:disable-non-selected="loading || submitLoading"
			class="mb-2"
			@change="updateFieldsFromDraft"
		/>

		<!-- inputs -->
		<v-row class="mt-0 mb-5">
			<!-- input data -->
			<v-col lg="9" md="8" cols="12" order-md="0" order="1" class="pb-md-0">
				<v-row dense class="align-center mt-0 mb-2">
					<!-- title -->
					<v-col :sm="useInHomePage ? 4 : 6" cols="12">
						<v-text-field
							v-model="title"
							:label="$t('inputs.title')"
							hide-details
							outlined
							dense
							class="rounded-medium"
							:loading="loading"
							:disabled="loading || submitLoading"
							:rules="rules.required"
						></v-text-field>
					</v-col>

					<!-- date-of-publication -->
					<v-col lg="3" sm="6" cols="12">
						<!-- date -->
						<v-menu
							ref="menu"
							:close-on-content-click="false"
							transition="scale-transition"
							offset-y
							min-width="auto"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="date"
									:label="$t('inputs.date')"
									hide-details
									outlined
									dense
									class="rounded-medium"
									prepend-inner-icon="mdi-calendar"
									clearable
									:loading="loading"
									:disabled="loading || submitLoading"
									readonly
									v-bind="attrs"
									v-on="on"
								></v-text-field>
							</template>
							<v-date-picker
								v-model="date"
								:max="(new Date().getFullYear() + 1) + new Date().toISOString().slice(4, 10)"
								min="1950-01-01"
								@change="$refs.menu.save(date)"
							></v-date-picker>
						</v-menu>
					</v-col>

					<!-- is-featured -->
					<v-col md="auto" sm="3" cols="auto">
						<v-switch
							v-model="isFeatured"
							:label="$t('inputs.is-featured')"
							hide-details
							:disabled="loading || submitLoading"
							class="mt-sm-0 mt-0 pt-0"
						></v-switch>
					</v-col>

					<!-- is home -->
					<v-col v-if="useInHomePage" md="auto" sm="3" cols="auto">
						<v-switch
							v-model="inHomePage"
							:label="$t('inputs.is-in-home-page')"
							hide-details
							:disabled="loading || submitLoading"
							class="mt-sm-0 mt-0 pt-0"
						></v-switch>
					</v-col>
					
					<!-- summary -->
					<v-col cols="12">
						<v-textarea
							v-model="summary"
							:label="$t('inputs.summary')"
							auto-grow
							no-resize
							rows="1"
							hide-details
							outlined
							dense
							class="rounded-medium"
							:loading="loading"
							:disabled="loading || submitLoading"
						></v-textarea>
					</v-col>

					<!-- filters & filters tree-->
					<template v-if="showFilters && useFiltersFor.news">
						<!-- filters -->
						<v-col v-if="!useFilterTree" lg="4" sm="6" cols="12">
							<v-autocomplete
								v-model="filterIds"
								:label="$t('inputs.filter')"
								:items="filters"
								item-text="name"
								item-value="id"
								multiple
								hide-details
								outlined
								dense
								chips
								small-chips
								deletable-chips
								class="rounded-medium"
								:loading="loading || assetsLoading"
								:disabled="loading || submitLoading"
							></v-autocomplete>
						</v-col>
						
						<!-- filters tree -->
						<v-col v-else lg="4" sm="6" cols="12">
							<v-tree-autocomplete
								v-model="filterIds"
								:label="$t('inputs.filter')"
								:items="filters"
								:treeItems="hierarchyFilters"
								chips
								small-chips
								deletable-chips
								dense
								hide-details
								outlined
								:loading="loading || assetsLoading"
								:disabled="loading || submitLoading"
								input-class="rounded-medium"
							></v-tree-autocomplete>
						</v-col>
					</template>
				</v-row>

				<!-- benefits -->
				<template v-if="useBenefits">
					<div class="d-flex align-center mb-3">
						<span class="text-body-1 font-weight-bold text-capitalize">
							{{$t('titles.benefits')}}
						</span>
						<v-btn
							icon
							:disabled="loading || submitLoading"
							@click="benefitsDialog = true"
						>
							<v-icon>mdi-plus-circle-outline</v-icon>
						</v-btn>
					</div>

					<!-- table -->
					<v-data-table
						:headers="benefitsHeaders"
						:items="loading ? [] : benefitsEntries"
						:items-per-page="-1"
						:loading="loading"
						:loader-height="2"
						dense
						disable-sort
						hide-default-footer
						class="rounded-medium mb-3 elevation-2"
					>
						<!-- image -->
						<template v-slot:item.image="{ item }">
							<bee-drop-img
								:value="item.image || require('@/assets/placeholder-image.png')"
								border-width="0"
								radius="11"
								:height="50"
								:width="50"
								contain
								hide-details
								hide-image-name
								hide-drag-drop-text
								disabled
								style="z-index: 0;"
							></bee-drop-img>
						</template>
						
						<!-- title -->
						<template v-slot:item.title="{ item }">
							{{item.title[lang]}}
						</template>
						
						<!-- type -->
						<template v-slot:item.description="{ item }">
							<div v-html="item.description[lang]" class="benefits-content"/>
						</template>

						<!-- actions -->
						<template v-slot:item.actions="{ item, index }">
							<div
								class="d-flex justify-center align-center"
								@dragover.prevent
								@drop="getDataOnDrop($event, index, upSellingFormEntries)"
							>
								<!-- edit -->
								<tooltip :text="$t('tooltip.edit')">
									<v-btn
										color="blue darken-4"
										dark
										small
										class="ms-1 px-1 flex-shrink-1"
										min-width="20px"
										:disabled="loading || submitLoading"
										@click="benefitsEntryId = item.id; benefitsDialog = true;"
									><v-icon>mdi-pencil</v-icon></v-btn>
								</tooltip>

								<!-- delete -->
								<tooltip :text="$t('tooltip.delete')">
									<v-btn
										color="red darken-1"
										dark
										small
										class="ms-1 px-1"
										min-width="20px"
										:disabled="loading || submitLoading"
										@click="benefitsEntries.splice(index, 1);"
									>
										<v-icon>mdi-delete</v-icon>
									</v-btn>
								</tooltip>

								<!-- change-order -->
								<tooltip v-if="false" :text="$t('tooltip.change-order')">
									<div
										draggable
										@dragstart="setDataOnDrag($event, index)"
									>
										<v-icon>mdi-drag-vertical</v-icon>
									</div>
								</tooltip>
							</div>
						</template>
					</v-data-table>
				</template>
				
				<!-- SEO fields -->
				<template v-if="showSEO">
					<div class="text-body-1 font-weight-bold mb-3">
						{{$t('SEO-fields')}}
					</div>
					<v-row dense>
						<!-- SEO keywords -->
						<v-col cols="12">
							<v-chips-input
								v-model="SEOkeywords"
								:label="$t('inputs.seo-keywords')"
								hide-details
								outlined
								dense
								class="rounded-medium"
								:loading="loading"
								:disabled="loading || submitLoading"
							></v-chips-input>
						</v-col>

						<!-- SEO description -->
						<v-col cols="12">
							<v-textarea
								v-model="SEOdescription"
								:label="$t('inputs.seo-description')"
								outlined
								dense
								auto-grow
								rows="1"
								no-resize
								counter
								:hint="SEOdescription && SEOdescription.length > 155 ? 'It\'s best to keep descriptions between 155-160 characters' : null"
								persistent-hint
								:color="SEOdescription && SEOdescription.length > 155 ? 'warning' : null"
								class="rounded-medium"
								:loading="loading"
								:disabled="loading || submitLoading"
								@keypress.enter.prevent
								@change="SEOdescription = SEOdescription.replace(/\n/g, ' ');"
							></v-textarea>
						</v-col>
					</v-row>
				</template>

				<!-- description -->
				<div class="text-body-1 font-weight-bold text-capitalize mb-3">{{$t('inputs.description')}}</div>
				<text-editor
					v-model="description"
					:loading="loading"
					:error="!Boolean(description) && isSubmitted"
					:use-template-builder="allowTemplateBuilder"
				/>
			</v-col>

			<v-col cols="auto" class="d-md-block d-none px-1">
				<v-divider vertical/>
			</v-col>

			<!-- drop image -->
			<v-col md cols="12" class="pb-md-0">
				<div class="sticky">
					<div class="text-body-1 font-weight-bold mb-3">
						{{$t('upload-images')}}
					</div>
					<bee-drop-img
						v-model="mainImage"
						dashed
						border-width="2"
						:border-color="isSubmitted && !mainImage ? '#f00' : '#ccc'"
						radius="11"
						height="250px"
						blurable
						clearable
						:loading="loading"
						:disabled="loading || submitLoading"
						:hide-image-name="false"
						disable-file-picker
						@remove-image="fileManagerFiles.mainImage = {}"
						@click="$eventBus.$emit('file-manager', { images: true }, (url, file) => {
							mainImage = url;
							fileManagerFiles.mainImage = file;
						})"
					>
						<template v-slot:drag-text>
							<span class="text-body-1 font-weight-medium">
								{{$t('drag-text')}}
							</span>
							<span class="text-caption mb-0">
								{{$t('supported-text')}}
							</span>
						</template>
					</bee-drop-img>
					
					<bee-drop-zone
						v-if="uploadMoreImages"
						v-model="images"
						dashed
						drop-zone-classes="pa-3"
						radius="11"
						border-width="2"
						max-height="250"
						:drop-image-props="{
							width: 70,
							height: 70
						}"
						:clear-icon="$route.params.id ? 'mdi-delete' : 'mdi-close'"
						:clear-color="$route.params.id ? 'red' : null"
						:gap-size="4"
						:drop-menu-width="$vuetify.breakpoint.mdAndUp ? 400 : '100%'"
						:drop-menu-max-width="$vuetify.breakpoint.mdAndUp ? 400 : '100%'"
						:drop-menu-left="null"
						show-add-btn
						show-drop-menu
						no-drop-replace
						class="mt-3"
						:loading="loading"
						:disabled="loading || submitLoading"
						disable-file-picker
						@remove-image="removeImage"
						@click="$eventBus.$emit('file-manager', { images: true, multiple: true }, (urls, files) => {
							images.push(...urls);
							fileManagerFiles.moreImages.push(...files);
						})"
					>
						<template v-slot:drag-text>
							<p class="mb-0">
								<span class="d-block text-body-1 font-weight-bold">
									{{$t('upload-more-image')}}
								</span>
								<span class="d-block text-caption mb-0">{{$t('drag-text')}}</span>
							</p>
						</template>
					</bee-drop-zone>
				</div>
			</v-col>
		</v-row>
		<v-btn
			type="submit"
			color="primary"
			class="rounded-medium me-2"
			:loading="submitLoading"
			:disabled="loading || submitLoading"
		>
			{{$t('save')}}
		</v-btn>
		<v-btn
			color="white"
			class="rounded-medium"
			:to="{ name: backRouteName }"
			:disabled="loading || submitLoading"
		>
			{{$t('back')}}
		</v-btn>
	</v-form>
</template>

<script>
import moment from 'moment';
import rules from '@/helpers/validation rules'
import { filters, news, SEO } from '@/configs/routes.config';
import { defaultLang, langs, multiLangInputs } from '@/configs/translates.config';
import { allowTemplateBuilder, useBenefitsFor, useFiltersFor, useFilterTree, useInHomePage, useUploadBenefitImageFor, useUploadMoreImages } from '@/configs/global.config';
import langsDraft from '@/mixins/langs-draft';
import { createObjectFromLangs } from '@/helpers/functions';
import { mapState } from 'vuex';

export default {
	name: 'NewsIndex',
	mixins: [langsDraft],
	
	data: () => ({
		// helpers
		loading: false,
		assetsLoading: false,
		submitLoading: false,
		isSubmitted: false,
		productImages: [],
		lang: defaultLang,
		backRouteName: news.name,
		rules,

		// configs
		showSEO: SEO.show,
		showFilters: filters.show,
		useFiltersFor,
		useFilterTree,
		uploadMoreImages: useUploadMoreImages.news,
		useInHomePage,
		multiLangInputs,
		allowTemplateBuilder,
		useBenefitImage: useUploadBenefitImageFor.news,
		useBenefits: useBenefitsFor.news,

		// benefits
		benefitsDialog: false,
		benefitsEntries: [],

		benefitsFormLang: defaultLang,
		benefitsEntryId: null,
		benefitsTitle: null,
		benefitsData: {
			image: null,
			fileManagerImage: {},
			title: createObjectFromLangs(),
			description: createObjectFromLangs({}, ''),
		},

		// file manager
		fileManagerFiles: {
			mainImage: {},
			moreImages: [],
		},
		
		// draft for languages 
		draft: {},
		draftNames: [
			'title',
			'summary',
			'SEOkeywords',
			'SEOdescription',
			{ name: 'description', value: '' }
		],

		// data
		title: null,
		summary: null,
		filterIds: [],
		date: null,
		isFeatured: null,
		inHomePage: null,
		description: null,
		mainImage: null,
		images: [],
		SEOkeywords: null,
		SEOdescription: null,
	}),

	watch: {
		$route(newVal) {
			if (newVal.params.id) {
				this.loading = true;
				this.fetchNews().finally(() => {
					this.loading = false;
				});
			}
		},

		benefitsDialog(val) {
			if (!val) {
				this.benefitsEntryId = null;
				this.benefitsData.image = null;
				this.benefitsData.fileManagerImage = {};
				this.benefitsData.title = createObjectFromLangs();
				this.benefitsData.description = createObjectFromLangs({}, '');	
				this.$refs.benefitsForm.reset();
			} else {
				this.benefitsFormLang = this.lang;

				if (this.benefitsEntryId) {
					const benefit = this.benefitsEntries.find(c => c.id === this.benefitsEntryId);

					this.benefitsData.image = benefit.image;
					this.benefitsData.fileManagerImage = benefit.fileManagerImage;
					this.benefitsData.title = benefit.title;
					this.benefitsData.description = benefit.description;
				}
			}
		}
	},

	computed: {
		...mapState({
			filters: state => state.filters.filters,
		}),
		
		acceptedToSaveLangs() {
			const acceptedLangs = langs.reduce((res, a) => { res[a.key] = false; return res }, {})
			langs.forEach(lang => {
				if (this.draft.title[lang.key] && this.draft.description[lang.key]) {
					acceptedLangs[lang.key] = true;
				}
			});
			return acceptedLangs;
		},

		benefitsHeaders() {
			const headers = [
				this.useBenefitImage ? { text: this.$t('headers.image'), value: 'image', width: 60 } : null,
				{ text: this.$t('headers.title'), value: 'title', width: 200 },
				{ text: this.$t('headers.description'), value: 'description' },
				{ text: this.$t('headers.actions'), value: 'actions', width: 120, align: 'center' },
			].filter(Boolean);
			
			for (let i = 0; i < headers.length; i++) {
				headers[i].class = 'px-2';
				headers[i].cellClass = 'px-2';
			}
			return headers
		},
	},

	methods: {
		// benefits
		saveBenefitEntry() {
			if (this.$refs.benefitsForm.validate()) {
				if (!this.benefitsEntryId) {
					this.benefitsEntries.push({
						id: Date.now(),
						image: this.benefitsData.image,
						fileManagerImage: this.benefitsData.fileManagerImage,
						title: this.benefitsData.title,
						description: this.benefitsData.description,
					});	
				} else {
					const index = this.benefitsEntries.findIndex(c => c.id === this.benefitsEntryId);
					this.benefitsEntries[index].image = this.benefitsData.image;
					this.benefitsEntries[index].fileManagerImage = this.benefitsData.fileManagerImage;
					this.benefitsEntries[index].title = this.benefitsData.title;
					this.benefitsEntries[index].description = this.benefitsData.description;
				}
				this.benefitsDialog = false;
			};
		},

		async submit() {
			this.isSubmitted = true;
			if (this.$refs.form.validate() && this.mainImage && this.description) {
				this.submitLoading = true;
				// more images
				const images = this.images.filter(c => typeof c !== 'string');

				const promises = [];
				let imagesPromise = null;
				let itemData = null;

				const submitRequest = (
					isDefaultOne,
					{ title, summary, description, SEOkeywords, SEOdescription, lang },
					id = this.$route.params.id
				) => {
					const actionName = id ? 'update' : 'create';
					return this.$store.dispatch(`news/${actionName}`, {
						id: id,
						title: title,
						summary: summary,
						filterIds: this.filterIds,
						date: this.date,
						isFeatured: this.isFeatured,
						inHomePage: this.inHomePage,
						content: description,
						image: 
							isDefaultOne && typeof this.mainImage !== 'string'
								? this.mainImage
								: null,
						imagePath: 
							isDefaultOne && typeof this.mainImage === 'string'
								? this.fileManagerFiles.mainImage.publicPath
								: null,
						imageName: 
							isDefaultOne && typeof this.mainImage === 'string'
								? this.fileManagerFiles.mainImage.fileName
								: null,
						benefits: this.benefitsEntries.map(c => ({
							image:
								isDefaultOne && typeof c.image !== 'string'
									? c.image
									: null,
							imagePath:
								isDefaultOne && typeof c.image === 'string'
									? c.fileManagerImage.publicPath
									: null,
							imageName:
								isDefaultOne && typeof c.image === 'string'
									? c.fileManagerImage.fileName
									: null,
							title: c.title[lang],
							description: c.description[lang],
						})),
						keywordsSeo: SEOkeywords,
						descriptionSeo: SEOdescription,
						acceptLanguage: lang
					}).then((data) => {
						// upload the other images
						if (isDefaultOne && (images.length || this.fileManagerFiles.moreImages.length)) {
							imagesPromise = this.$store.dispatch('news/uploadImages', {
								id: data.id,
								images,
								imagesPaths: this.fileManagerFiles.moreImages.map(c => c.publicPath),
								imagesNames: this.fileManagerFiles.moreImages.map(c => c.fileName),
							})
						}
						return data;
					})
				}

				const filteredLangs = langs.filter(c => multiLangInputs || c.key === defaultLang);
				for (let index = 0; index < filteredLangs.length; index++) {
					const lang = filteredLangs[index].key;

					const title = this.draft.title[lang];
					const summary = this.draft.summary[lang];
					const SEOkeywords = this.draft.SEOkeywords[lang];
					const SEOdescription = this.draft.SEOdescription[lang];
					const description = this.draft.description[lang];

					const dataDueLang = { title, summary, SEOkeywords, SEOdescription, description, lang };

					if (title && description) {
						if (!itemData) {
							await submitRequest(!itemData, dataDueLang).then(data => { itemData = data });
						} else {
							promises.push(submitRequest(false, dataDueLang, itemData.id));
						}
					}
				}

				const successHandler = () => {
					const message =
						this.$route.params.id
							? this.$t('$-is-updated-successfully', { name: this.$t('News.news') })
							: this.$t('$-is-created-successfully', { name: this.$t('News.news') });
					this.$eventBus.$emit('show-snackbar', false, message);
					this.$router.push({ name: news.name });
				}
				const errorHandler = () => {
					if (itemData) {
						this.$router.replace({
							name: this.$route.name,
							params: { slug: itemData.slug, id: itemData.id }
						});
					}
				}

				Promise.all(promises).then(() => {
					if (!imagesPromise) successHandler();
					else {
						this.$eventBus.$emit('show-snackbar', false, this.$t('please-wait-uploading-the-images'));
						imagesPromise.catch(errorHandler).then(successHandler);
					}
				}).catch(
					errorHandler
				).finally(() => {
					if (!imagesPromise) this.submitLoading = false;
					else imagesPromise.finally(() => { this.submitLoading = false });
				})
			}
		},

		// FIXME show dialog to confirm remove
		removeImage(image) {
			if (this.$route.params.id) {
				const imageId = this.productImages.find(c => c.url === image)?.id;

				if (imageId) {
					this.$store.dispatch('news/deleteImage', {
						id: this.$route.params.id,
						imageId: imageId
					})
				}
			}
			
			const fileManagerImageId = this.fileManagerFiles.moreImages.find(c => c.url === image)?.id;
			if (fileManagerImageId) {
				const imageIndex = this.fileManagerFiles.moreImages.findIndex(c => c.id === fileManagerImageId);
				this.fileManagerFiles.moreImages.splice(imageIndex, 1);
			}
		},

		fetchNews() {
			const promises = [];

			langs.filter(c => multiLangInputs || c.key === defaultLang).forEach((c, index) => {
				promises.push(
					this.$store.dispatch('news/fetchBySlug', {
						slug: this.$route.params.slug,
						acceptLanguage: c.key
					}).then((data) => {
						if (index === 0) {
							this.date = data.date ? moment(data.date).format('YYYY-MM-DD') : null;
							this.isFeatured = data.isFeatured;
							this.inHomePage = data.inHomePage;
							this.mainImage = data.primaryImage.url;
							this.images = data.images.map(c => c.url).filter(c => c !== data.primaryImage.url);

							this.productImages = data.images.filter(c => c !== data.primaryImage.url);

							if (this.showFilters && useFiltersFor.news) {
								this.filterIds = data.filterIds;
							}
						}
						this.draft.title[c.key] = data.title;
						this.draft.summary[c.key] = data.summary;
						this.draft.description[c.key] = data.content;
						this.draft.SEOkeywords[c.key] = data.keywordsSeo;
						this.draft.SEOdescription[c.key] = data.descriptionSeo;

						// benefits
						if (this.useBenefits) {
							if (!this.benefitsEntries.length) {
								this.benefitsEntries = data.benefits.map((e, index) => ({
									...e,
									image: e.image?.url,
									fileManagerImage: {},
									title: createObjectFromLangs({ [c.key]: e.title }),
									description: createObjectFromLangs({ [c.key]: e.description }),	
								}));
							} else {
								for (let i = 0; i < data.benefits.length; i++) {
									this.benefitsEntries[i].title[c.key] = data.benefits[i].title;	
									this.benefitsEntries[i].description[c.key] = data.benefits[i].description;	
								}
							}
						}
					})
				)
			});

			return Promise.all(promises).then(() => {
				this.title = this.draft.title[this.lang];
				this.summary = this.draft.summary[this.lang];
				this.description = this.draft.description[this.lang];
				this.SEOkeywords = this.draft.SEOkeywords[this.lang];
				this.SEOdescription = this.draft.SEOdescription[this.lang];
			})
		}
	},

	created() {
		// initializing draft for languages 
		if (this.$route.params.id) {
			this.loading = true;
			this.fetchNews().finally(() => {
				this.loading = false;
			})
		}

		if (this.showFilters && useFiltersFor.news && !this.filters.length) {
			this.assetsLoading = true;
			Promise.all([
				!this.filters.length ? this.$store.dispatch('filters/fetchAll') : null,
				useFilterTree && !this.filters.length ? this.$store.dispatch('filters/fetchAllAsHierarchy') : null
			]).finally(() => {
				this.assetsLoading = false;
			}); 
		}
	}
};
</script>

<style lang="scss">
</style>